// Notification bell — shared by every shell. Polls /notifications, shows an
// unread badge, and opens a dropdown of recent items. Marking all read on open.
// docs/replan-v2.md R10. Renders generically from the payload's titleKey/bodyKey.

import { useT } from '@fajrarabi/i18n/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { listNotifications, markNotificationsRead, type Notification } from '~/lib/messaging';
import { isSubscribed, pushSupported, subscribeToPush, unsubscribeFromPush } from '~/lib/push';

export function NotificationBell({ variant = 'chrome' }: { variant?: 'chrome' | 'kid' }) {
  const t = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const query = useQuery({
    queryKey: ['notifications'],
    queryFn: ({ signal }) => listNotifications(signal),
    refetchInterval: 20_000,
  });
  const unread = query.data?.unreadCount ?? 0;

  // Push opt-in state (kid shell never shows it — `variant === 'kid'` hides the
  // footer, since kids don't manage device permissions).
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => {
    if (!pushSupported()) return;
    void isSubscribed().then(setPushOn);
  }, []);
  async function togglePush() {
    setPushBusy(true);
    try {
      if (pushOn) {
        await unsubscribeFromPush();
        setPushOn(false);
      } else {
        setPushOn(await subscribeToPush());
      }
    } finally {
      setPushBusy(false);
    }
  }

  const markRead = useMutation({
    mutationFn: () => markNotificationsRead(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) markRead.mutate();
  }

  const kid = variant === 'kid';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        aria-label={t('app.notif.bell_label')}
        className={
          kid
            ? 'relative grid h-11 w-11 place-items-center rounded-full bg-cream-50 text-xl ring-1 ring-burgundy-500/10 transition-colors hover:bg-cream-200'
            : 'relative grid h-9 w-9 place-items-center rounded-full bg-cream-100 text-base ring-1 ring-burgundy-500/10 transition-colors hover:bg-cream-200'
        }
      >
        <span aria-hidden="true">🔔</span>
        {unread > 0 && (
          <span className="absolute -end-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-heart px-1 text-[10px] font-bold text-cream-50">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute end-0 z-40 mt-2 w-80 max-w-[90vw] overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_-24px_rgba(31,26,23,0.4)] ring-1 ring-burgundy-500/10">
          <div className="border-b border-burgundy-500/10 px-4 py-3">
            <p className="text-sm font-semibold text-ink-900">{t('app.notif.title')}</p>
          </div>
          <ul className="max-h-96 overflow-auto">
            {(query.data?.notifications ?? []).length === 0 ? (
              <li className="px-4 py-8 text-center text-sm text-ink-500">{t('app.notif.empty')}</li>
            ) : (
              query.data!.notifications.map((n) => <NotificationItem key={n.id} n={n} />)
            )}
          </ul>

          {!kid && pushSupported() && (
            <button
              type="button"
              onClick={() => void togglePush()}
              disabled={pushBusy}
              className="flex w-full items-center justify-between border-t border-burgundy-500/10 px-4 py-3 text-start text-sm text-ink-700 transition-colors hover:bg-cream-100 disabled:opacity-50"
            >
              <span>{t('app.notif.push_label')}</span>
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
                  pushOn ? 'bg-leaf/20 text-leaf' : 'bg-cream-200 text-ink-500'
                }`}
              >
                {pushOn ? t('app.notif.push_on') : t('app.notif.push_off')}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationItem({ n }: { n: Notification }) {
  const t = useT();
  const params = n.payload.params ?? {};
  // Announcements carry literal admin-written text (not translation keys), so
  // render them verbatim; everything else resolves via t().
  const literal = n.kind === 'announcement';
  const title = n.payload.titleKey
    ? literal
      ? n.payload.titleKey
      : t(n.payload.titleKey, params)
    : n.kind;
  const body = n.payload.bodyKey
    ? literal
      ? n.payload.bodyKey
      : t(n.payload.bodyKey, params)
    : '';
  return (
    <li className={`px-4 py-3 ${n.readAt ? '' : 'bg-sun-50/60'}`}>
      <p className="text-sm font-semibold text-ink-900">{title}</p>
      {body && <p className="mt-0.5 text-sm text-ink-500">{body}</p>}
    </li>
  );
}
